// @ts-strict-ignore
import EmptyState from './EmptyState'
import Filters from './Filters'
import StoreCard from './StoreCard'
import StoresContainer from './StoresContainer'
import { FilterState, reducer } from './reducer'
import { useUrlSync } from './useUrlSync'
import { MultipleItemResponse } from '@lib/kontent'
import { BrandPage } from '@lib/kontent/models/BrandPage'
import { StoreService } from '@lib/kontent/models/StoreService'
import { getUnixTime } from 'date-fns'
import { format } from 'date-fns-tz'
import { useTranslation } from 'next-i18next'
import { FC, useReducer } from 'react'

export type OpeningHours = {
  [day: number]: {
    from?: number
    till?: number
    closedText?: string
  }
}

export type GridItem = {
  urlSlug: string
  title: string
  country: string
  address: string
  image: string
  openingHours: OpeningHours
  openendTodayTill: number | null
  openingHoursOverwrite?: string
  services: string[]
  brands: string[]
}

function filter(items: GridItem[], filters: FilterState): GridItem[] {
  let result = items
  if (filters.country.length) {
    result = result.filter((item) => filters.country.includes(item.country))
  }
  if (filters.opened.length) {
    const todayIndex = parseInt(format(new Date(), 'i'), 10) - 1
    if (filters.opened.includes('opened-now')) {
      const now = getUnixTime(new Date())
      result = result.filter((item) => {
        if (
          item.openingHours[todayIndex] &&
          item.openingHours[todayIndex].from &&
          item.openingHours[todayIndex].till
        ) {
          return (
            now >= item.openingHours[todayIndex].from && now <= item.openingHours[todayIndex].till
          )
        }
        return false
      })
    }
    if (filters.opened.includes('opened-evening')) {
      result = result.filter((item) => {
        if (item.openingHours[todayIndex] && item.openingHours[todayIndex].till) {
          const tonight = getUnixTime(new Date().setHours(18))
          return item.openingHours[todayIndex].till > tonight
        }
        return false
      })
    }
    if (filters.opened.includes('opened-sunday')) {
      result = result.filter((item) => item.openingHours[6].from)
    }
  }
  if (filters.services.length) {
    result = result.filter((item) => {
      const overlap = filters.services.filter((service) => item.services.includes(service))
      return overlap.length > 0
    })
  }
  if (filters.brands.length) {
    result = result.filter((item) => {
      const overlap = filters.brands.filter((brand) => item.brands.includes(brand))
      return overlap.length > 0
    })
  }
  return result
}

type Props = {
  netherlands: GridItem[]
  singapore: GridItem[]
  brands?: MultipleItemResponse<BrandPage>
  services?: MultipleItemResponse<StoreService>
  initialFilters: {
    [key: string]: string[]
  }
}

const StoresGrid: FC<Props> = (props) => {
  const {
    i18n: { language },
  } = useTranslation()
  const [state, dispatch] = useReducer(reducer, {
    country: [],
    opened: [],
    services: [],
    brands: [],
    ...props.initialFilters,
  })
  const netherlands = filter(props.netherlands, state)
  const singapore = filter(props.singapore, state)
  const totalStores = netherlands.length + singapore.length

  useUrlSync(state)
  return (
    <>
      <Filters
        dispatch={dispatch}
        filters={state}
        brands={props.brands.items}
        services={props.services.items}
        totalStores={totalStores}
      />
      {netherlands.length > 0 && (
        <StoresContainer
          pb={{ _: '1.5rem', medium: '4rem', large: '7rem' }}
          label={language === 'nl' ? 'Nederland' : 'Netherlands'}
          defaultIsOpen
        >
          {netherlands.map((store) => (
            <StoreCard
              key={store.urlSlug}
              urlSlug={store.urlSlug}
              country={store.country}
              image={store.image}
              title={store.title}
              address={store.address}
              openendTodayTill={store.openendTodayTill}
              openingHoursOverwrite={store.openingHoursOverwrite}
            />
          ))}
        </StoresContainer>
      )}

      {singapore.length > 0 && (
        <StoresContainer
          py={{ _: '1.5rem', medium: '4rem', large: '7rem' }}
          backgroundColor="pampasLight"
          label="Singapore"
        >
          {singapore.map((store) => (
            <StoreCard
              key={store.urlSlug}
              urlSlug={store.urlSlug}
              country={store.country}
              image={store.image}
              title={store.title}
              address={store.address}
              openendTodayTill={store.openendTodayTill}
              openingHoursOverwrite={store.openingHoursOverwrite}
            />
          ))}
        </StoresContainer>
      )}

      {totalStores === 0 && <EmptyState onClick={() => dispatch({ type: 'clear-filters' })} />}
    </>
  )
}

export default StoresGrid
